/**
 * Procedurele SVG-kaartart (fase 7).
 *
 * Per kaarttype een eigen beeldtaal, deterministisch geseed op de kaart-id:
 * dezelfde kaart ziet er altijd identiek uit, en alle kaarten van één chakra
 * delen palet/stijl (kleur + accent uit de header). Volledig offline.
 *
 * INRUIL-HAAK: zet voor een kaart een statische afbeelding op
 * `/assets/cards/<id>.webp` (bv. later AI-gegenereerd) en `KaartArt` toont die
 * i.p.v. de SVG. Zo kun je per kaart of helemaal overstappen zonder code-wijziging.
 */
import type { Kaart, KaartType, Origin } from '../../data/types';
import { HEADERS } from '../../data/headers';

/**
 * INRUIL-MANIFEST: id's van kaarten waarvoor een statische afbeelding bestaat in
 * `/assets/cards/<id>.webp` (bv. later AI-gegenereerd). Nu leeg → alles SVG.
 * Voeg id's toe (of zet `ALLE_STATISCH` op true als je voor élke kaart een webp
 * hebt) om over te stappen, zonder verdere code-wijziging.
 */
const STATISCHE_ART = new Set<string>();
const ALLE_STATISCH = false;
function heeftStatischeArt(id: string): boolean {
  return ALLE_STATISCH || STATISCHE_ART.has(id);
}

/* — deterministische RNG uit de kaart-id — */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}
function maakRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface ArtProps {
  kleur: string;
  accent: string;
  origin: Origin;
  rng: () => number;
}

/* ---------------- generatoren per type ---------------- */

function Steen({ kleur, accent, rng }: ArtProps) {
  const n = 5 + Math.floor(rng() * 3);
  const rot = rng() * Math.PI * 2;
  const pts = Array.from({ length: n }, (_, i) => {
    const a = rot + (i / n) * Math.PI * 2;
    const r = 24 + rng() * 9;
    return [50 + Math.cos(a) * r, 48 + Math.sin(a) * r] as const;
  });
  const poly = pts.map((p) => p.join(',')).join(' ');
  return (
    <g>
      <polygon points={poly} fill={kleur} stroke={accent} strokeWidth={1.5} />
      {pts.map((p, i) => {
        const q = pts[(i + 1) % n];
        return <polygon key={i} points={`50,48 ${p.join(',')} ${q.join(',')}`} fill={accent} opacity={i % 2 ? 0.28 : 0.12} />;
      })}
      <polygon points={`50,48 ${pts[0].join(',')} ${pts[1].join(',')}`} fill="#fff" opacity={0.5} />
    </g>
  );
}

function Orgaan({ kleur, accent, rng }: ArtProps) {
  const dx = 6 + rng() * 4;
  const dy = 4 + rng() * 4;
  return (
    <g stroke={accent} strokeWidth={1.4} fill={kleur} fillOpacity={0.85}>
      <path d={`M${50 - dx},38 C${30 - dx},38 ${28},66 ${50 - dx + 4},66 C${56},66 ${52},48 ${50},48 Z`} />
      <path d={`M${50 + dx},38 C${70 + dx},38 ${72},66 ${50 + dx - 4},66 C${44},66 ${48},48 ${50},48 Z`} />
      <polyline points={`24,${52 + dy} 40,${52 + dy} 46,42 52,62 58,${52 + dy} 76,${52 + dy}`} fill="none" stroke="#fff" strokeOpacity={0.55} strokeWidth={1.6} />
    </g>
  );
}

function Praktijk({ kleur, accent, rng }: ArtProps) {
  const ringen = 2 + Math.floor(rng() * 2);
  return (
    <g>
      {Array.from({ length: ringen }, (_, i) => (
        <circle key={i} cx={50} cy={50} r={18 + i * 9} fill="none" stroke={accent} strokeOpacity={0.3 - i * 0.07} strokeWidth={1.2} />
      ))}
      {/* mediterende figuur */}
      <g fill={accent}>
        <circle cx={50} cy={36} r={6} />
        <path d="M38,64 C40,50 60,50 62,64 Z" />
        <path d="M34,64 C42,58 58,58 66,64 L64,68 L36,68 Z" fill={kleur} stroke={accent} strokeWidth={1} />
      </g>
    </g>
  );
}

const SIGIL: Record<Origin, (c: string, a: string) => JSX.Element> = {
  1: (c, a) => <g stroke={a} strokeWidth={2} fill="none"><polygon points="50,68 28,34 72,34" fill={c} fillOpacity={0.4} /><line x1="34" y1="50" x2="66" y2="50" /></g>,
  2: (c, a) => <g stroke={a} strokeWidth={2}><polygon points="50,68 28,34 72,34" fill={c} fillOpacity={0.5} /></g>,
  3: (c, a) => <g stroke={a} strokeWidth={2}><polygon points="50,32 28,66 72,66" fill={c} fillOpacity={0.5} /></g>,
  4: (c, a) => <g stroke={a} strokeWidth={2} fill="none"><polygon points="50,32 28,66 72,66" fill={c} fillOpacity={0.4} /><line x1="36" y1="52" x2="64" y2="52" /></g>,
  5: (c, a) => <g stroke={a} strokeWidth={2} fill="none"><circle cx="50" cy="50" r="20" fill={c} fillOpacity={0.4} /><circle cx="50" cy="50" r="4" fill={a} /></g>,
  6: (c, a) => <g stroke={a} strokeWidth={2} fill="none"><path d="M26,50 Q50,30 74,50 Q50,70 26,50 Z" fill={c} fillOpacity={0.4} /><circle cx="50" cy="50" r="6" fill={a} /></g>,
  7: (c, a) => <g stroke={a} strokeWidth={1.5}>{Array.from({ length: 12 }, (_, i) => { const ang = (i / 12) * Math.PI * 2; return <line key={i} x1={50} y1={50} x2={50 + Math.cos(ang) * 24} y2={50 + Math.sin(ang) * 24} />; })}<circle cx="50" cy="50" r="7" fill={c} /></g>,
};

function Element({ kleur, accent, origin }: ArtProps) {
  return SIGIL[origin](kleur, accent);
}

function Mantra({ kleur, accent, origin }: ArtProps) {
  const mantra = HEADERS[origin].mantra;
  return (
    <g>
      {[16, 24, 32].map((r, i) => (
        <circle key={i} cx={50} cy={50} r={r} fill="none" stroke={accent} strokeOpacity={0.4 - i * 0.1} strokeWidth={1.2} />
      ))}
      <circle cx={50} cy={50} r={15} fill={kleur} opacity={0.5} />
      <text x={50} y={50} textAnchor="middle" dominantBaseline="central" fill="#fff" style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, fontSize: mantra.length > 2 ? 11 : 14 }}>
        {mantra}
      </text>
    </g>
  );
}

function Kleur({ kleur, accent, rng }: ArtProps) {
  const stralen = 8 + Math.floor(rng() * 5);
  return (
    <g>
      <defs>
        <radialGradient id={`kb-${Math.floor(rng() * 1e6)}`}>
          <stop offset="0%" stopColor="#fff" stopOpacity={0.9} />
          <stop offset="40%" stopColor={kleur} stopOpacity={0.9} />
          <stop offset="100%" stopColor={kleur} stopOpacity={0} />
        </radialGradient>
      </defs>
      {Array.from({ length: stralen }, (_, i) => {
        const a = (i / stralen) * Math.PI * 2;
        return <line key={i} x1={50} y1={50} x2={50 + Math.cos(a) * 34} y2={50 + Math.sin(a) * 34} stroke={accent} strokeOpacity={0.5} strokeWidth={1.4} />;
      })}
      <circle cx={50} cy={50} r={22} fill={kleur} opacity={0.85} />
      <circle cx={50} cy={50} r={10} fill="#fff" opacity={0.7} />
    </g>
  );
}

const GENERATOR: Record<KaartType, (p: ArtProps) => JSX.Element> = {
  s: Steen,
  o: Orgaan,
  p: Praktijk,
  e: Element,
  m: Mantra,
  k: Kleur,
};

interface Props {
  kaart: Kaart;
  kleur: string;
  accent: string;
  className?: string;
}

export function KaartArt({ kaart, kleur, accent, className }: Props) {
  // Inruil-haak: statische afbeelding (bv. AI-art) indien aanwezig, anders SVG.
  if (heeftStatischeArt(kaart.id)) {
    return <img src={`/assets/cards/${kaart.id}.webp`} alt="" className={className} style={{ objectFit: 'cover' }} />;
  }
  return <ArtSvg kaart={kaart} kleur={kleur} accent={accent} className={className} />;
}

function ArtSvg({ kaart, kleur, accent, className }: Props) {
  const rng = maakRng(hash(kaart.id));
  const Gen = GENERATOR[kaart.type];
  return (
    <svg viewBox="0 0 100 100" className={className} preserveAspectRatio="xMidYMid meet" aria-hidden>
      <defs>
        <radialGradient id={`bg-${kaart.id}`}>
          <stop offset="0%" stopColor={kleur} stopOpacity={0.45} />
          <stop offset="100%" stopColor={kleur} stopOpacity={0.05} />
        </radialGradient>
      </defs>
      <rect x={0} y={0} width={100} height={100} fill={`url(#bg-${kaart.id})`} />
      <Gen kleur={kleur} accent={accent} origin={kaart.origin} rng={rng} />
    </svg>
  );
}
